import React, { useState, useCallback } from "react";
import { Card, Text, Button, ButtonVariant, Badge } from "@merge-api/merge-javascript-shared";
import useAppContext from "../../../../context/useAppContext";
import { showErrorToast, showSuccessToast } from "../../../../shared/Toasts";
import { fetchWithAuth, fetchCurrentUser } from "../../../../../api-client/APIClient";
import ChangePasswordModal from "../modals/ChangePasswordModal";
import TwoFAModal from "../modals/TwoFAModal";
import CurrentTwoFACodeModal from "../modals/CurrentTwoFACodeModal";

const getTotpErrorText = (response: Response | undefined) =>
  response?.status === 400
    ? "Invalid two-factor authentication code. Please try again."
    : "Something went wrong. Please try again.";

const SecurityCard = () => {
  // CONTEXT
  const { user, setUser } = useAppContext();

  // STATE
  const [configURL2FA, setConfigURL2FA] = useState<null | string>(null);

  const [isModifying2FA, setIsModifying2FA] = useState<boolean>(false);

  const [showChangePasswordModal, setShowChangePasswordModal] = useState<boolean>(false);
  const [showTwoFAModal, setShowTwoFAModal] = useState<boolean>(false);
  const [showModify2FAModal, setShowModify2FAModal] = useState<boolean>(false);
  const [showDisable2FAModal, setShowDisable2FAModal] = useState<boolean>(false);

  // HANDLERS
  const onClickEnable2FA = useCallback(() => {
    fetchWithAuth({
      path: "/users/totp/create",
      method: "POST",
      onResponse: (data) => {
        setConfigURL2FA(data.config_url);
      },
      onError: () => {
        showErrorToast("Oops, something went wrong! Please try again.");
      },
    });
    setShowTwoFAModal(true);
  }, []);

  const onClickModify2FA = useCallback(
    (currentTotpToken: string, onDone: (errorText?: string) => void) => {
      fetchWithAuth({
        path: "/users/totp/modify",
        method: "POST",
        body: {
          current_totp_token: currentTotpToken,
        },
        onResponse: (data) => {
          onDone();
          setIsModifying2FA(true);
          setConfigURL2FA(data.config_url);
          setShowTwoFAModal(true);
        },
        onError: (response) => {
          onDone(getTotpErrorText(response));
        },
      });
    },
    [],
  );

  const onClickDisable2FA = useCallback(
    (currentTotpToken: string, onDone: (errorText?: string) => void) => {
      fetchWithAuth({
        path: "/users/totp/disable",
        method: "POST",
        body: {
          current_totp_token: currentTotpToken,
        },
        onResponse: () => {
          onDone();
          fetchCurrentUser((response) => {
            setUser(response);
            showSuccessToast("Two-factor authentication disabled successfully!");
          });
        },
        onError: (response) => {
          onDone(getTotpErrorText(response));
        },
      });
    },
    [user],
  );

  return (
    <>
      <Card variant="shadow">
        <div className="w-full flex flex-col">
          {/* header */}
          <div className="px-6 py-5 border-b-solid border-b-gray-10 border-b-[1px]">
            <Text variant="h5">Security</Text>
          </div>

          {/* body */}
          <div className="flex flex-col px-6 py-5 justify-between gap-y-5">
            {/* password */}
            <div className="flex flex-row justify-between items-center">
              <Text variant="h6">Password</Text>
              <div className="flex flex-row gap-3">
                <Button
                  variant={ButtonVariant.TertiaryWhite}
                  size="sm"
                  onClick={() => setShowChangePasswordModal(true)}
                >
                  Change password
                </Button>
                <ChangePasswordModal
                  isShown={showChangePasswordModal}
                  onHide={() => setShowChangePasswordModal(false)}
                  user={user}
                />
              </div>
            </div>

            {/* 2fa */}
            <div className="flex flex-row gap-12 items-center">
              <div className="flex flex-col">
                <div className="flex flex-row items-center gap-2">
                  <Text variant="h6">Two-factor authentication</Text>
                  {user.is_two_factor_enabled && <Badge color="green">Enabled</Badge>}
                </div>
                <Text>
                  Merge supports 2FA with an authenticator app. We recommend using Google
                  Authenticator, which is available on iOS and Android
                </Text>
              </div>
              {user.is_two_factor_enabled && !user.organization.is_mfa_required ? (
                <Button
                  variant={ButtonVariant.DangerFilled}
                  size="sm"
                  onClick={() => setShowDisable2FAModal(true)}
                >
                  Disable 2FA
                </Button>
              ) : user.is_two_factor_enabled && user.organization.is_mfa_required ? (
                <Button
                  size="sm"
                  variant={ButtonVariant.SecondaryGray}
                  onClick={() => setShowModify2FAModal(true)}
                >
                  Change 2FA device
                </Button>
              ) : configURL2FA ? (
                <Button
                  variant={ButtonVariant.TertiaryWhite}
                  size="sm"
                  onClick={() => setConfigURL2FA(null)}
                >
                  Cancel
                </Button>
              ) : (
                <Button size="sm" onClick={onClickEnable2FA} variant={ButtonVariant.TertiaryWhite}>
                  Set up
                </Button>
              )}
              <TwoFAModal
                isShown={showTwoFAModal}
                onHide={() => {
                  setShowTwoFAModal(false);
                  setConfigURL2FA(null);
                }}
                isModifying2FA={isModifying2FA}
                configURL2FA={configURL2FA}
                setConfigURL2FA={setConfigURL2FA}
              />
              <CurrentTwoFACodeModal
                isShown={showDisable2FAModal}
                onHide={() => setShowDisable2FAModal(false)}
                title="Disable two-factor authentication"
                description="Enter the code from your current authenticator app to disable 2FA. Your account will only be protected by your password."
                primaryButtonText="Disable 2FA"
                primaryButtonVariant={ButtonVariant.DangerFilled}
                onSubmit={onClickDisable2FA}
              />
              <CurrentTwoFACodeModal
                isShown={showModify2FAModal}
                onHide={() => setShowModify2FAModal(false)}
                title="Change 2FA device"
                description="Enter the code from your current authenticator app to continue."
                primaryButtonText="Continue"
                primaryButtonVariant={ButtonVariant.PrimaryGray}
                onSubmit={onClickModify2FA}
              />
            </div>
          </div>
        </div>
      </Card>
    </>
  );
};

export default SecurityCard;
